import React, { createContext, useContext, useEffect, useSyncExternalStore, useState } from 'react';
import axios from 'axios';
import { getSnapshot, subscribe, setTerminology } from './store';

// Use the same bearer token written by Login; older sessions may have sanctum_token only.
const terminologyApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/webmanager`, withCredentials: true });
const token = () => localStorage.getItem('token') || localStorage.getItem('sanctum_token');
terminologyApi.interceptors.request.use(config => {
  const bearer = token();
  if (bearer) config.headers.Authorization = `Bearer ${bearer}`;
  config.headers.Accept = 'application/json';
  return config;
});
export const saveTerminology = (labels, revision) => terminologyApi.put('/terminology', { labels, revision });
export const resetTerminology = revision => terminologyApi.post('/terminology/reset', { revision });
const Context = createContext({ refresh: async () => {}, error: '', loading: false });
export function useTerminology() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { ...state, ...useContext(Context) };
}
export function TerminologyProvider({ children }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    if (!token()) { setTerminology({}); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await terminologyApi.get('/terminology');
      setTerminology(data);
      setError('');
    } catch (err) {
      setError('Unable to load terminology. Existing display defaults remain available.');
    } finally { setLoading(false); }
  };
  useEffect(() => {
    let previousToken;
    let stopped = false;
    let sequence = 0;
    const load = async () => {
      const bearer = token();
      const request = ++sequence;
      if (!bearer) { setTerminology({}); setError(''); setLoading(false); return; }
      setLoading(true);
      try {
        const { data } = await terminologyApi.get('/terminology');
        if (!stopped && request === sequence && bearer === token()) { setTerminology(data); setError(''); }
      } catch { if (!stopped) setError('Unable to load terminology. Existing display defaults remain available.'); }
      finally { if (!stopped && request === sequence) setLoading(false); }
    };
    const checkAuth = () => {
      const current = token();
      if (current !== previousToken) { previousToken = current; setTerminology({}); load(); }
    };
    checkAuth();
    const authTimer = setInterval(checkAuth, 1000);
    const refreshTimer = setInterval(load, 60000);
    window.addEventListener('focus', load);
    window.addEventListener('storage', checkAuth);
    return () => { stopped = true; sequence++; clearInterval(authTimer); clearInterval(refreshTimer); window.removeEventListener('focus', load); window.removeEventListener('storage', checkAuth); };
  }, []);
  return <Context.Provider value={{ refresh, error, loading }}>{children}</Context.Provider>;
}
